/**
 * useRazorpay
 * -----------
 * Lazily injects Razorpay `checkout.js` once, and exposes an
 * `openCheckout({...})` function that opens the standard checkout modal
 * with all our brand colours + buyer details pre-filled.
 *
 * The KEY_SECRET NEVER touches the frontend — we only receive the
 * public `key_id` from our backend along with the razorpay_order_id.
 */
import { useCallback, useEffect, useRef, useState } from "react";

const SCRIPT_SRC = "https://checkout.razorpay.com/v1/checkout.js";

// Module-scope promise so multiple concurrent callers share the same load.
let scriptPromise = null;

function loadRazorpayScript() {
  if (typeof window === "undefined") return Promise.resolve(false);
  if (window.Razorpay) return Promise.resolve(true);
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve) => {
    const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(true));
      existing.addEventListener("error", () => resolve(false));
      return;
    }
    const s = document.createElement("script");
    s.src = SCRIPT_SRC;
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => {
      scriptPromise = null; // let retry
      resolve(false);
    };
    document.body.appendChild(s);
  });
  return scriptPromise;
}

export default function useRazorpay() {
  const [ready, setReady] = useState(() => typeof window !== "undefined" && !!window.Razorpay);
  const alive = useRef(true);

  useEffect(() => () => { alive.current = false; }, []);

  const ensureLoaded = useCallback(async () => {
    if (window.Razorpay) return true;
    const ok = await loadRazorpayScript();
    if (alive.current) setReady(ok && !!window.Razorpay);
    return ok && !!window.Razorpay;
  }, []);

  /**
   * openCheckout — hand it the backend's create-order response and get
   * back a Promise that resolves with the razorpay success payload
   * (razorpay_order_id, razorpay_payment_id, razorpay_signature) or
   * rejects with `{ code: "cancelled" | "failed", message }`.
   */
  const openCheckout = useCallback(async ({ order, name = "Jeweltix", description = "Design purchase", theme = "#c9a24c", onBeforeOpen } = {}) => {
    const ok = await ensureLoaded();
    if (!ok) throw new Error("Razorpay checkout could not be loaded — check your network");
    if (!order || !order.razorpay_order_id) throw new Error("Missing Razorpay order");

    return new Promise((resolve, reject) => {
      const rzp = new window.Razorpay({
        key: order.key_id,
        order_id: order.razorpay_order_id,
        amount: order.amount,
        currency: order.currency || "INR",
        name,
        description,
        image: undefined,
        prefill: {
          name: order.buyer?.name || "",
          email: order.buyer?.email || "",
          contact: order.buyer?.contact || "",
        },
        notes: { our_tx_id: order.our_tx_id },
        theme: { color: theme },
        modal: {
          ondismiss: () => reject({ code: "cancelled", message: "You cancelled the payment." }),
          escape: true,
          confirm_close: true,
        },
        handler: (response) => resolve(response),
      });
      rzp.on("payment.failed", (resp) => {
        reject({
          code: "failed",
          message: resp?.error?.description || "Payment failed. Please try again.",
          error: resp?.error,
        });
      });
      if (typeof onBeforeOpen === "function") onBeforeOpen(rzp);
      rzp.open();
    });
  }, [ensureLoaded]);

  return { ready, openCheckout };
}
